import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { IconButton } from "../IconButton";
import FormLoggingSupport from "./components/FormLoggingSupport";
import LoggingSupportUser from "./components/LoggingSupportUser";
import { useLocation } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { CREATE_TICKET } from "../../../graphql/mutation/Support";
import useUser from "../../../hooks/useUser";

const PREFIX_ENUM = {
  development: "DEV: ",
  staging: "STG: ",
  production: "",
};

const getTitle = title =>
  PREFIX_ENUM[process.env.NODE_ENV || "development"].concat(title);

const LoggingSupport = props => {
  // eslint-disable-next-line
  const { onSubmit: openAlert } = props;
  const ref = useRef(null);
  const buttonWrapperRef = useRef(null);
  const [formState, setFormState] = useState(false);
  const [step, setStep] = useState("support");
  const [supportActionTarget, setSupportActionTarget] = useState(null);
  const [hasResolvedHeaderTarget, setHasResolvedHeaderTarget] = useState(false);
  const location = useLocation();
  const [createTicket] = useMutation(CREATE_TICKET);
  const { user } = useUser();
  const isHeaderMounted = Boolean(supportActionTarget);

  const onSubmit = data => {
    const transformedData = {
      name: getTitle(data.ticketTitle),
      issue_type: data.issueType,
      category: {
        name: data.categoryName,
      },
      ...(data.subCategoryName
        ? { subcategory: { name: data.subCategoryName } }
        : {}),
      description: data.description,
      user_agent: window.navigator.userAgent,
      url: location.pathname,
      referring_url: location.pathname,
      local_time: new Date(),
      requester: {
        email: user?.email,
      },
    };
    createTicket({
      variables: {
        input: transformedData,
      },
    })
      .then(() => {
        openAlert({
          text: "Your ticket was successfully created.",
          isOpen: true,
          type: "info",
        });
      })
      .catch(() => {
        openAlert({
          text: "There was an error creating the ticket, please try again later.",
          isOpen: true,
          type: "warning",
        });
      });
    setFormState(false);
    setStep("support");
  };

  const handleClickOutside = e => {
    if (
      ref?.current?.contains(e.target) ||
      buttonWrapperRef?.current?.contains(e.target)
    ) {
      return;
    }
    if (ref?.current && !ref.current.contains(e.target)) {
      setFormState(false);
      setStep("support");
    }
  };
  const getElement = () => {
    switch (step) {
      case "support":
        return (
          <FormLoggingSupport
            position={isHeaderMounted ? "top-right" : "bottom-left"}
            nextStep={e => {
              setStep("log");
              e.stopPropagation();
            }}
            onSubmit={onSubmit}
          />
        );
      case "log":
      default:
        return (
          <LoggingSupportUser
            onSubmit={onSubmit}
            position={isHeaderMounted ? "top-right" : "bottom-left"}
          />
        );
    }
  };
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const updateSupportActionTarget = () => {
      setSupportActionTarget(document.getElementById("header-support-action"));
      setHasResolvedHeaderTarget(true);
    };

    updateSupportActionTarget();

    const observer = new MutationObserver(updateSupportActionTarget);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  const supportButton = (
    <div ref={buttonWrapperRef}>
      <IconButton
        variant="clean"
        title="Support"
        onClick={() => setFormState(!formState)}
        className={
          isHeaderMounted
            ? "rounded-full p-1 text-gray-700 shadow-[inset_0_0_0_2px_rgb(55,65,81)] hover:shadow-[inset_0_0_0_2px_rgb(107,114,128)] hover:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
            : "absolute bottom-8 left-3 z-[2] cursor-pointer"
        }
      >
        <span
          aria-hidden="true"
          className="flex h-6 w-6 items-center justify-center text-xl font-medium leading-none"
        >
          ?
        </span>
      </IconButton>
    </div>
  );

  return (
    <div ref={ref}>
      {hasResolvedHeaderTarget
        ? isHeaderMounted
          ? createPortal(supportButton, supportActionTarget)
          : supportButton
        : null}
      {formState && getElement()}
    </div>
  );
};

export default LoggingSupport;
